/**
 * Централізоване управління ключами localStorage
 */
export const LocalStorageKeys = {
  ORDERS_ACTIVE_TAB: 'ordersActiveTab',
  ORDER_FLOW_DRAFT: 'orderFlowDraft',
  IS_DEV_MODE: 'isDev',
  MENU_IS_OPENED: 'menuIsOpened',
  INVENTORY_FILTERS: 'inventoryFilters',
  WAREHOUSES_FILTERS: 'warehousesFilters',
} as const;

/**
 * Хелпер для роботи з localStorage
 */
export const localStorageHelper = {
  /**
   * Зберегти значення
   */
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Error saving to localStorage (key: ${key}):`, error);
    }
  },

  /**
   * Отримати значення
   */
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Error reading from localStorage (key: ${key}):`, error);
      return null;
    }
  },

  /**
   * Видалити значення
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing from localStorage (key: ${key}):`, error);
    }
  },

  /**
   * Зберегти об'єкт як JSON
   */
  setObject<T>(key: string, value: T): void {
    try {
      const jsonValue = JSON.stringify(value);
      localStorage.setItem(key, jsonValue);
    } catch (error) {
      console.error(`Error saving object to localStorage (key: ${key}):`, error);
    }
  },

  /**
   * Отримати об'єкт з JSON
   */
  getObject<T>(key: string): T | null {
    try {
      const jsonValue = localStorage.getItem(key);
      if (jsonValue) {
        return JSON.parse(jsonValue) as T;
      }
      return null;
    } catch (error) {
      console.error(`Error parsing object from localStorage (key: ${key}):`, error);
      return null;
    }
  },

  /**
   * Зберегти значення з TTL (Time To Live)
   * @param key - Ключ
   * @param value - Значення
   * @param expiresAt - Timestamp (мс) коли дані мають бути видалені
   */
  setWithTTL<T>(key: string, value: T, expiresAt: number): void {
    try {
      const item = {
        value,
        expiresAt,
      };
      localStorage.setItem(key, JSON.stringify(item));
    } catch (error) {
      console.error(`Error saving to localStorage with TTL (key: ${key}):`, error);
    }
  },

  /**
   * Отримати значення з перевіркою TTL
   * Автоматично видаляє застарілі дані
   * @param key - Ключ
   * @returns Значення або null якщо дані застаріли/відсутні
   */
  getWithTTL<T>(key: string): T | null {
    try {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;

      const item = JSON.parse(itemStr);

      if (Date.now() > item.expiresAt) {
        localStorage.removeItem(key);
        return null;
      }

      return item.value as T;
    } catch (error) {
      console.error(`Error reading from localStorage with TTL (key: ${key}):`, error);
      localStorage.removeItem(key);
      return null;
    }
  },
};

/**
 * Dev-прапорець: пропуск примусового logout (напр. 403 деактивації профілю).
 * Увімкнути: `localStorageHelper.set(LocalStorageKeys.IS_DEV_MODE, 'true')`
 * або в консолі: `localStorage.setItem('isDev', 'true')` (те саме, що `LocalStorageKeys.IS_DEV_MODE`).
 */
export function isDevModeEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorageHelper.get(LocalStorageKeys.IS_DEV_MODE) === 'true';
}

/**
 * Menu items gated behind `?isDev=true`. Reads the current URL first so the
 * item appears on the same navigation that sets the flag (localStorage is
 * written asynchronously by `useDevMode`).
 */
export function isDevMenuVisible(): boolean {
  if (typeof window === 'undefined') return false;
  const param = new URLSearchParams(window.location.search).get('isDev');
  if (param === 'true') return true;
  if (param === 'false') return false;
  return isDevModeEnabled();
}

